import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NavigationStart, Router } from '@angular/router';
import { Department, Designation, InviteTeacherRequest } from '@edtech-ui/core/models/admin-panel/invite-teacher.model';
import { TeacherManagementService } from '@edtech-ui/core/services/admin-panel/teacherManagement.service';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-teacher-invite-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './teacher-invite-dialog.component.html',
  styleUrl: './teacher-invite-dialog.component.scss'
})
export class TeacherInviteDialogComponent {
  private destroy$ = new Subject<void>();
  fullName = '';
  email = '';
  departmentId: number | string = '';
  roleId: number | string = '';
  honorific = '';
  isLoading = false;



  // Options for dropdowns
  departments: Department[] = [];
  roles: Designation[] = [];
  honorifics = ['Dr.', 'Mr.', 'Ms.', 'Mrs.', 'Prof.'];
  // Search text
  searchDepartment = '';
  searchRole = '';

  // Filtered lists
  filteredDepartments = [...this.departments];
  filteredRoles = [...this.roles];

  constructor(private dialogRef: MatDialogRef<TeacherInviteDialogComponent>,
    private teacherManagementService: TeacherManagementService,
    private toastr: ToastrService,
    private router: Router

  ) { }
  ngOnInit(): void {
    this.fetchDepartments();
    this.fetchRoles();
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.dialogRef.close();
        }
      });

  }

  fetchDepartments() {
    this.teacherManagementService.getDepartments().subscribe({
      next: (res) => {
        if (res.Success && res.Data) {
          this.departments = res.Data;
        } else {
          this.toastr.error(res.Message || 'Failed to load departments.');
        }
      },
      error: (err) => {
        this.toastr.error('Something went wrong while fetching departments.');
      }
    });
  }

  fetchRoles() {
    this.teacherManagementService.getDesignations().subscribe({
      next: (res) => {
        if (res.Success && res.Data) {
          this.roles = res.Data;
        } else {
          this.toastr.error(res.Message || 'Failed to load roles.');
        }
      },
      error: (err) => {
        this.toastr.error('Something went wrong while fetching roles.');
      }
    });
  }



  close() {
    this.dialogRef.close();
  }
  sendInvite() {
    const name = this.fullName?.trim() || '';
    const email = this.email?.trim() || '';

    if (!this.honorific || !name || !email || !this.departmentId || !this.roleId) {
      this.toastr.warning('Please fill all required fields.');
      return;
    }

    const nameRegex = /^[A-Za-z][A-Za-z\s'.-]{1,}$/;
    if (!nameRegex.test(name)) {
      this.toastr.warning('Please enter a valid full name.');
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      this.toastr.warning('Please enter a valid email.');
      return;
    }

    let selectedDept = this.departments.find(d => d.id == this.departmentId);
    const selectedRole = this.roles.find(r => r.id == this.roleId);

    if (this.departmentId === 'Other') {
      if (!this.customDepartmentName?.trim()) {
        this.toastr.warning('Please enter a department name.');
        return;
      }

      selectedDept = {
        id: 0,
        name: this.customDepartmentName.trim()
      } as Department;
    }

    if (!selectedDept) {
      this.toastr.error('Invalid department selected.');
      return;
    }

    if (!selectedRole) {
      this.toastr.error('Invalid role selected.');
      return;
    }

    const payload: InviteTeacherRequest = {
      department_id: this.departmentId === 'Other' ? 'Other' : String(selectedDept.id),
      department_name: selectedDept.name,
      designation: selectedRole.name,
      email,
      full_name: name,
      honorific: this.honorific
    };

    this.isLoading = true;

    this.teacherManagementService
      .inviteTeacher(payload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          if (res.Success) {
            this.toastr.success(res.Message || 'Invitation sent successfully!');
            this.dialogRef.close(true);
          } else {
            this.toastr.error(res.Message || 'Failed to send invitation.');
          }
        },
        error: () => {
          this.toastr.error('Something went wrong. Please try again later.');
        }
      });
  }



  customDepartmentName = '';

  onDepartmentChange(value: string | number) {
    if (value !== 'Other') {
      this.customDepartmentName = '';
    }
  }


  // Filter Department dropdown based on search input
  filterDepartments() {
    const val = this.searchDepartment.toLowerCase();
    this.filteredDepartments = this.departments.filter(d =>
      d.name.toLowerCase().includes(val)
    );
  }

  // Filter Role dropdown based on search input
  filterRoles() {
    const val = this.searchRole.toLowerCase();
    this.filteredRoles = this.roles.filter(r =>
      r.name.toLowerCase().includes(val)
    );
  }

  // Optional: Reset search when dropdown closes
  onOpenDepartment(opened: boolean) {
    if (!opened) {
      this.searchDepartment = '';
      this.filteredDepartments = [...this.departments];
    }
  }

  onOpenRole(opened: boolean) {
    if (!opened) {
      this.searchRole = '';
      this.filteredRoles = [...this.roles];
    }
  }
  getDepartmentName(id: number | string): string {
    if (id === 'Other') return 'Other';
    const dept = this.departments.find(d => d.id === id);
    return dept ? dept.name : 'Select Department';
  }

  getRoleName(id: number | string): string {
    const role = this.roles.find(r => r.id === id);
    return role ? role.name : 'Select Role';
  }
}
